import React, { useState } from 'react';
import { Box, Typography, Collapse, IconButton, Paper } from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const JsonValue = ({ value }) => {
  if (typeof value === 'string') {
    return <span style={{ color: '#2e7d32' }}>"{value}"</span>;
  }
  if (typeof value === 'number') {
    return <span style={{ color: '#ed6c02' }}>{value}</span>;
  }
  if (typeof value === 'boolean') {
    return <span style={{ color: '#d32f2f' }}>{value ? 'true' : 'false'}</span>;
  }
  if (value === null) {
    return <span style={{ color: '#9e9e9e', fontStyle: 'italic' }}>null</span>;
  }
  return <span>{String(value)}</span>;
};

const JsonNode = ({ keyName, value, currentDepth, initialDepth }) => {
  const isObject = value !== null && typeof value === 'object';
  const isArray = Array.isArray(value);
  
  const [open, setOpen] = useState(currentDepth < initialDepth);

  if (!isObject) {
    return (
      <Box sx={{ display: 'flex', pl: 1, py: 0.5, fontFamily: 'monospace', fontSize: '0.9rem' }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#000000ff', mr: 1 }}>
          {keyName !== null ? `"${keyName}":` : ''}
        </Typography>
        <Typography variant="body2">
          <JsonValue value={value} />
          {','}
        </Typography>
      </Box>
    );
  }

  const isEmpty = Object.keys(value).length === 0;
  const bracketOpen = isArray ? '[' : '{';
  const bracketClose = isArray ? ']' : '}';

  return (
    <Box sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
      <Box 
        onClick={() => !isEmpty && setOpen(!open)}
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          cursor: isEmpty ? 'default' : 'pointer',
          '&:hover': { bgcolor: isEmpty ? 'transparent' : 'rgba(0,0,0,0.04)' },
          py: 0.5,
          borderRadius: 1,
          marginLeft: isEmpty ? 1 : 0
        }}
      >
        <IconButton size="small" sx={{ p: 0.5, display: isEmpty ? 'none' : 'inline-flex' }}>
          {open ? <KeyboardArrowDownIcon fontSize="inherit" /> : <KeyboardArrowRightIcon fontSize="inherit" />}
        </IconButton>
        
        {keyName !== null && (
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#000000ff', mr: 1 }}>
            "{keyName}":
          </Typography>
        )}
        
        <Typography variant="body2" color="text.secondary">
          {bracketOpen} {isEmpty ? bracketClose : (!open && ` ... ${bracketClose}`)}
          {!open && !isEmpty && isArray && <span style={{ marginLeft: 8, fontSize: '0.8em' }}>({value.length} items)</span>}
        </Typography>
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box 
          sx={{ 
            pl: 2, 
            ml: 1.5, 
            borderLeft: '1px dashed rgba(0, 0, 0, 0.2)',
          }}
        >
          {Object.entries(value).map(([k, v]) => (
            <JsonNode 
              key={k} 
              keyName={isArray ? null : k}
              value={v} 
              currentDepth={currentDepth + 1} 
              initialDepth={initialDepth} 
            />
          ))}
        </Box>
        {!isEmpty && (
            <Box sx={{ pl: 1 }}>
            <Typography variant="body2" color="text.secondary">
                {bracketClose},
            </Typography>
            </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default function JsonDisplay({ root, maxDepth = 2 }) {

  return (
    <Paper elevation={3} sx={{ p: 3, bgcolor: '#fafafa', borderRadius: 2, overflowX: 'auto' }}>
      <JsonNode 
        keyName="root" 
        value={root} 
        currentDepth={0} 
        initialDepth={maxDepth} 
      />
    </Paper>
  );
}